import styles from "./FloatingBubble.module.scss";
import FloatingIcon from "../../imgs/smallSupport.svg";
import Support from "../../imgs/SupportPhoto.png";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Popover } from "@mui/material";
import heic2any from "heic2any";
import Swal from "sweetalert2";
import { useAppSelector } from "../../redux/store";
import moment from "moment";
import { fetchNewMessages, readMessages, sendMessage, setChatIsOpen } from "../../redux/slices/chatSlice";
import { useDispatch } from "react-redux";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroller";
import { useTranslation } from "react-i18next";

export default function FloatingBubble({loading,setLoading,hasMore, setHasMore,platform_id}) {
  const {t} = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null);
  const chatIsOpen = useAppSelector((state) => state.chat.chatIsOpen);
  const [start, setStart] = useState(true);
  const [failedMessage, setFailedMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [filePreviews, setFilePreviews] = useState([]);
  const [activeMsgId, setActiveMsgId] = useState(null);
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);
  const historyMessages = useAppSelector((state) => state.chat.messages);
  const unReadCount = useAppSelector((state) => state.chat.unReadCount);
  const dispatch = useDispatch();
  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day} ${month} ${year} ${hours}:${minutes}`;
  };
  const handleSendMessage = () => {
    if (message.trim() === "" && selectedFiles.length === 0) return;

    const images = [];
    const files = [];

    selectedFiles?.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        images.push(imageUrl);
      } else {
        files.push(file.name);
      }
    });

    dispatch(sendMessage({ msg: { content: message, date_time: Date.now()}}))
    setMessage("");
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    const generatePreviews = async () => {
      if (!selectedFiles || selectedFiles.length === 0) {
        setFilePreviews([]);
        return;
      }

      const previews = await Promise.all(
        selectedFiles.map(async (file) => {
          if (
            file.type.startsWith("image/") ||
            file.name.toLowerCase().endsWith(".heic") ||
            file.type === "application/pdf"
          ) {
            try {
              if (
                file.type === "image/heic" ||
                file.name.toLowerCase().endsWith(".heic")
              ) {
                const convertedBlob = await heic2any({
                  blob: file,
                  toType: "image/jpeg",
                  quality: 0.8,
                });
                return {
                  file,
                  previewUrl: URL.createObjectURL(convertedBlob),
                };
              }

              return {
                file,
                previewUrl: URL.createObjectURL(file),
              };
            } catch (err) {
              console.error("Ошибка при обработке файла:", file.name, err);
              return null;
            }
          } else {
            return null;
          }
        })
      );

      const validPreviews = previews.filter(Boolean);
      setFilePreviews(validPreviews);
    };

    generatePreviews();

    return () => {
      filePreviews?.forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [selectedFiles]);

  const contentRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const prevLenRef    = useRef(0);

  useEffect(() => {
    if (!chatIsOpen) return;
    const el = contentRef.current;
    if (!el) return;

    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      isAtBottomRef.current = dist < 50;
    };

    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [chatIsOpen]);

  useLayoutEffect(() => {
    if (!chatIsOpen) return;
    const el = contentRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
  }, [chatIsOpen]);

  useLayoutEffect(() => {
    if (!chatIsOpen) {
      prevLenRef.current = historyMessages.length;
      return;
    }

    const el = contentRef.current;
    if (!el) return;

    const prevLen = prevLenRef.current;
    const currLen = historyMessages.length;

    if (currLen > prevLen && isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }

    prevLenRef.current = currLen;
  }, [historyMessages.length, chatIsOpen]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    const showError = (message) => {
      setFailedMessage(message);
      setTimeout(() => setFailedMessage(null), 4000);
    };

    if (files.length > 5) {
      showError("Вы можете выбрать не более 5 файлов за один раз.");
      event.target.value = "";
      return;
    }

    const allowedExtensions = /\.(png|jpg|jpeg|pdf|doc|docx|heic)$/i;
    const maxSize = 1024 * 1024 * 25;

    for (let file of files) {
      if (file.size > maxSize) {
        showError(`Файл "${file.name}" слишком большой (максимум 25MB).`);
        event.target.value = "";
        return;
      }

      if (!allowedExtensions.test(file.name)) {
        showError(
          `Недопустимый тип файла "${file.name}". Пожалуйста, выберите PNG, JPG, JPEG, PDF, DOC, DOCX.`
        );
        event.target.value = "";
        return;
      }
    }

    setSelectedFiles(files);
  };

  const [loaded, setLoaded] = useState(false);

  const LoadMoreMessages = async () => {
    if(hasMore && !loading){
      setLoading(true)
        const currentCount   = historyMessages.length;
        const itemsPerPage   = 20;
        const pageToFetch = Math.floor(currentCount / itemsPerPage);

        try {
            const {hasNext} = await dispatch(fetchNewMessages({ page: pageToFetch, platform_id })).unwrap();
            setHasMore(hasNext);
            setLoading(false)
        } catch (error) {
            console.error('Ошибка при загрузке уведомлений:', error);
            setHasMore(false);
        }finally{
          setLoading(false)
        }
    }
};
  // const fetchHistoryMessages = async () => {
  //   setLoading(true);
  //   try{
  //     const response = await axios.post(`${process.env.REACT_APP_CHAT_SERVICE_HTTP}/init/cabinet/messages`, {platform_id: `lead_100`,page:0,limit:20});
  //     if(response.data && response.data.pageDocs.length > 0) {
  //       setLoading(false);
  //     }
  //   }catch (error) {
  //     setLoading(false);
  //     console.error(error);
  //   }
  // }

  useEffect(() => {
    if(chatIsOpen){
      dispatch(readMessages());
      if(!historyMessages || historyMessages.length === 0) {
        console.log(true)
              LoadMoreMessages()
      }
    }
  }, [chatIsOpen]);
  useEffect(()=>{
    if(historyMessages.length > 0) setLoading(false)
  },[historyMessages])

  return (
    <div className={styles.floating_bubble}>
      <button
        className={styles.floating_button}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          dispatch(setChatIsOpen(!chatIsOpen));
        }}
      >
        <img src={FloatingIcon} width={48} height={48} />
        {
          unReadCount > 0 && (
            <span className={styles.unread}>
              {unReadCount > 99 ? "99" : unReadCount}
            </span>
          )
        }
      </button>
      <Popover
        anchorEl={anchorEl}
        open={chatIsOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disablePortal
        PopperProps={{
          strategy: 'fixed',            // position: fixed вместо absolute
          modifiers: [
            { name: 'eventListeners', enabled: false },  // отключаем слушатели resize/scroll
            { name: 'flip', enabled: false },            // отключаем подстановку альтернативных позиций
          ],
        }}
        onClose={() => {
          dispatch(setChatIsOpen(false));
          setSuccessMessage(false);
        }}
        TransitionProps={{
            onEntered:()=>{
              let el = contentRef.current
              if(el){
                el.scrollTop = el.scrollHeight;
              }
            }
        }}
      >
        <div className={styles.popover}>
          <div className={styles.header}>
            <img width="24px" height="24px" src={FloatingIcon} />
            <span>{t("Support")}</span>
            <button
              className={styles.close}
              onClick={() => {
                dispatch(setChatIsOpen(false));
                setSuccessMessage(false);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M5 11L11 5"
                  stroke="var(--nav-button-font-active)"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M5 5L11 11"
                  stroke="var(--nav-button-font-active)"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            {failedMessage && (
              <div className={styles.failed_message}>
                <div className={styles.top}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M8 5.16675V8.66675"
                      stroke="#FF453A"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14.0538 5.71994V10.2799C14.0538 11.0266 13.6538 11.7199 13.0071 12.0999L9.04712 14.3866C8.40045 14.7599 7.60044 14.7599 6.9471 14.3866L2.9871 12.0999C2.34043 11.7266 1.94043 11.0333 1.94043 10.2799V5.71994C1.94043 4.97327 2.34043 4.27991 2.9871 3.89991L6.9471 1.61325C7.59377 1.23992 8.39379 1.23992 9.04712 1.61325L13.0071 3.89991C13.6538 4.27991 14.0538 4.96661 14.0538 5.71994Z"
                      stroke="#FF453A"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8 10.8V10.8667"
                      stroke="#FF453A"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p>{t("Upload file")}</p>
                </div>
                <span>{t(failedMessage)}</span>
              </div>
            )}
          </div>
          <>
            <div className={styles.content} ref={contentRef}
            style={{
              maxHeight: 445,
              overflowY: 'scroll',
              display: 'flex',
            }}>
              
            <InfiniteScroll
                pageStart={1}
                loadMore={LoadMoreMessages}
                hasMore={hasMore}
                isReverse={true}
                threshold={200}
                useWindow={false}
                style={{width:'100%'}}
            >
            {successMessage && (
              <div className={styles.success_message}>
                <div className={styles.top}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.99967 14.6666C11.6663 14.6666 14.6663 11.6666 14.6663 7.99992C14.6663 4.33325 11.6663 1.33325 7.99967 1.33325C4.33301 1.33325 1.33301 4.33325 1.33301 7.99992C1.33301 11.6666 4.33301 14.6666 7.99967 14.6666Z"
                      stroke="#32D74B"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5.16699 7.99995L7.05366 9.88661L10.8337 6.11328"
                      stroke="#32D74B"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <p>{t("Successful")}</p>
                </div>
                <span>{t("Manager will call you soon")}</span>
              </div>
            )}
            {historyMessages?.length === 0 ? (
              <div style={{width:"100%" , display:'flex',alignItems:'center',justifyContent:'center', flexDirection:'column',height:"415px"}}>
                <img className={styles.support_photo} src={Support} />
                <span className={styles.support_subtitle}>
                  {t("Hello")} <br />{" "}
                  {t("How can I assist you?")}
                </span>
              </div>
            ) : (
              <div className={styles.day_messages}>
                {historyMessages?.map((msg) => (
                  <div
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (!msg.agent_id) {
                        setActiveMsgId(
                          activeMsgId === msg.id ? null : msg.id
                        );
                      }
                    }}
                    className={`${styles.message} ${
                      !msg.agent_id ? styles.own : styles.other
                    }`}
                  >
                    <div
                      className={`${styles.message_container} ${
                        !msg.agent_id ? styles.own : styles.other
                      }`}
                    >
                      <div className={styles.top}>
                        {msg.agent_id && (
                          <div className={styles.icon}>
                            <img src={Support} />
                          </div>
                        )}
                        <div className={styles.author}>{msg.agent_id?t("Manager"):t("Me")}</div>
                        <div className={styles.time}>
                          {moment(msg.date_time).local().format("DD.MM.YYYY HH:mm")}
                        </div>
                      </div>
                      <div className={styles.msg}>
                        <>
                          <div className={styles.text}>{msg.content}</div>

                          {msg.file && (
                            <a
                              className={styles.file}
                              href={`/path/to/files/${msg.file}`}
                              download
                            >
                              {msg.file}
                            </a>
                          )}
                          {msg?.images?.length > 0 && (
                            <div className={styles.image_group}>
                              {msg.images.map((img, idx) => {
                                const isBlob = img.startsWith("blob:");
                                const isRemote = img.startsWith("http");

                                const src = isBlob
                                  ? img
                                  : isRemote
                                  ? img
                                  : `/images/${img}`;
                                const filename = src.split("/").pop();

                                const imageElement = (
                                  <div
                                    className={styles.image_wrapper}
                                    key={idx}
                                  >
                                    <img
                                      src={src}
                                      alt={`image-${idx}`}
                                      onLoad={() => setLoaded(true)}
                                      onError={() => setLoaded(true)}
                                      className={
                                        loaded
                                          ? styles.img_loaded
                                          : styles.img_loading
                                      }
                                    />
                                    {!loaded && (
                                      <div className={styles.spinner_overlay}>
                                        <div className={styles.spinner}></div>
                                      </div>
                                    )}
                                  </div>
                                );

                                return isBlob || isRemote ? (
                                  <a
                                    href={src}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    key={idx}
                                  >
                                    {imageElement}
                                  </a>
                                ) : (
                                  <a href={src} download={filename} key={idx}>
                                    {imageElement}
                                  </a>
                                );
                              })}
                            </div>
                          )}
                        </>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </InfiniteScroll>
            </div>
            <div className={styles.footer}>
              {start && !historyMessages ? (
                <>
                  <div
                    className={styles.start_button}
                    onClick={() => {
                      setSuccessMessage(false);
                      setStart(false);
                    }}
                  >
                    <div className={styles.left}>
                      <p>{t("I need Support")}</p>
                      <span>{t("Start Chat")}</span>
                    </div>
                    <div className={styles.start_arrow}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M9.62012 3.95337L13.6668 8.00004L9.62012 12.0467"
                          stroke="white"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M2.33301 8H13.553"
                          stroke="white"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div
                    className={styles.start_button}
                    onClick={() => setSuccessMessage(true)}
                  >
                    <div className={styles.left}>
                      <p>{t("Callback")}</p>
                      <span>{t("Request a callback")}</span>
                    </div>
                    <div className={styles.start_arrow}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M9.62012 3.95337L13.6668 8.00004L9.62012 12.0467"
                          stroke="white"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M2.33301 8H13.553"
                          stroke="white"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.footer_container}>
                  {!historyMessages && (
                    <div className={styles.note}>
                      {t(
                        "Please describe your issue so that we can assist you, and a nearby staff member will contact you as soon as possible. Thank you for your patience."
                      )}
                    </div>
                  )}

                  <div className={styles.message_input}>
                    {/* <button
                      className={styles.add}
                      onClick={() => {
                        if (fileInputRef.current) {
                          fileInputRef.current.click();
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M11.9697 12V15.5C11.9697 17.43 13.5397 19 15.4697 19C17.3997 19 18.9697 17.43 18.9697 15.5V10C18.9697 6.13 15.8397 3 11.9697 3C8.09973 3 4.96973 6.13 4.96973 10V16C4.96973 19.31 7.65973 22 10.9697 22"
                          stroke="white"
                          stroke-opacity="0.85"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileSelect}
                        multiple
                      />
                    </button> */}
                    <input
                      type="text"
                      placeholder={t("Write your message...")}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <button
                      className={styles.send}
                      disabled={
                        message.trim() === "" &&
                        (!selectedFiles || selectedFiles.length === 0)
                      }
                      onClick={handleSendMessage}
                    >
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect width="32" height="32" rx="4" fill="#5E5CE6"/>
                    <path d="M17.6699 16.1616L11.4907 17.1918C11.4197 17.2037 11.353 17.234 11.2974 17.2798C11.2418 17.3256 11.1993 17.3852 11.174 17.4527L9.04349 23.1593C8.84003 23.6843 9.38887 24.1847 9.89259 23.9329L24.6596 16.5504C24.7619 16.4993 24.8479 16.4208 24.908 16.3235C24.9682 16.2263 25 16.1143 25 16C25 15.8857 24.9682 15.7736 24.908 15.6764C24.8479 15.5792 24.7619 15.5006 24.6596 15.4496L9.89259 8.06707C9.38887 7.81525 8.84003 8.31644 9.04349 8.84059L11.1749 14.5473C11.2 14.6149 11.2425 14.6746 11.2981 14.7206C11.3537 14.7665 11.4204 14.797 11.4915 14.8089L17.6707 15.8384C17.7087 15.845 17.7432 15.8649 17.768 15.8944C17.7928 15.924 17.8065 15.9614 17.8065 16C17.8065 16.0386 17.7928 16.0759 17.768 16.1055C17.7432 16.135 17.7087 16.1549 17.6707 16.1616H17.6699Z" fill="white" fill-opacity="0.85"/>
                    </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        </div>
      </Popover>
    </div>
  );
}
